import NativeOperationControls, { downloadNativeOperationReport } from '../../components/Apps/NativeOperationControls';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getRemoteEpg, watchRemoteEpg, getRemoteEpgOperation, getRemoteEpgOperationByRequest, continueRemoteEpgOperation, cancelRemoteEpgOperation, exportRemoteEpgOperation } from '../../components/Remote/deviceRemoteApi';
import './remoteEpg.css';
import RemoteEpgSearch from './RemoteEpgSearch';
import { watchReadinessText, watchSetupHref } from '../../components/Fixtures/watchReadiness';
import { venueDate, sportsWindow } from '../../components/Fixtures/sportsTime';

const HOUR = 3600000;
const WIDTH = 24 * 220;
const ROW_HEIGHT = 88;
const running = (operation) => ['pending', 'running'].includes(operation?.status);
export function epgShiftDay(date, days) {
  const shifted = new Date(`${date}T12:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}
export function epgDayLabel(date) {
  const value = new Date(`${date}T12:00:00Z`); const day = value.getUTCDate();
  const suffix = day % 100 >= 11 && day % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
  return `${value.toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short' })} ${day}${suffix}`;
}
export function epgProgrammeTone(title) {
  // Keep a show's colour stable when rows are virtualized or the guide refreshes.
  let hash = 0;
  for (const letter of String(title || '').trim().toLowerCase()) hash = (hash * 31 + letter.codePointAt(0)) >>> 0;
  return hash % 6;
}
const clock = (at, timeZone) => new Date(at).toLocaleTimeString('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const failureText = {
  sports_watch_number_ambiguous: 'The provider publishes this number for multiple channels. Choose another channel until its numbering is verified.',
  sports_watch_ads_clear_failed: 'The current ad display could not be stopped. Check the player connection.',
  sports_watch_state_failed: 'The app state could not be checked. Check the player connection.',
  sports_watch_key_failed: 'A channel key failed. Check the screen before retrying; keys are not automatically repeated.',
  sports_watch_configuration_changed: 'App or channel setup changed. Refresh the guide.',
  sports_watch_app_not_running: 'The app did not report running. Channel keys were stopped.',
  sports_watch_launch_failed: 'App launch rules did not finish. Check Site Control / Apps.',
  sports_watch_input_failed: 'The TV input routine did not finish. Check its saved training.',
  sports_watch_busy: 'Another channel change is running. Check the TV before retrying.',
};
export function epgProgrammeStyle(programme, start) {
  const from = Date.parse(programme.startsAt); const to = Date.parse(programme.endsAt);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from || to <= start || from >= start + 24 * HOUR) return null;
  return { left: `${100 * (Math.max(from, start) - start) / (24 * HOUR)}%`,
    width: `${100 * (Math.min(to, start + 24 * HOUR) - Math.max(from, start)) / (24 * HOUR)}%` };
}
export function epgAdDevice(devices, selectedTargetId) {
  return [...devices].filter((entry) => entry.suitestreamApp && entry.switchReady !== false
    && (Number(entry.target.id) === Number(selectedTargetId) || Number(entry.screen?.televisionDeviceId) === Number(selectedTargetId)))
    .sort((a, b) => Number(Number(b.target.id) === Number(selectedTargetId)) - Number(Number(a.target.id) === Number(selectedTargetId)))[0] || null;
}

function RemoteEpgView({ targetId, enabled, blocked, rotations = [], adDevice, adStatus, adMessage, onPlayAd, onBusyChange, onRefresh, navigation, onNavigationAccepted }) {
  const [view, setView] = useState({});
  const [data, setData] = useState({ channels: [], apps: [], timeZone: 'UTC' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selection, setSelection] = useState(null);
  const [reload, setReload] = useState(0);
  const [tick, setTick] = useState(Date.now());
  const [operation, setOperation] = useState(null);
  const [request, setRequest] = useState(null);
  const [sending, setSending] = useState(false);
  const [statusReload, setStatusReload] = useState(0);
  const [viewport, setViewport] = useState({ top: 0, height: 300 });
  const [searchTarget, setSearchTarget] = useState(null);
  const [searchReset, setSearchReset] = useState(0);
  const [navigationError, setNavigationError] = useState('');
  const [lastTuned, setLastTuned] = useState(null);
  const tuningRoute = useRef(null);
  const positionedTarget = useRef(null);
  const focusedTarget = useRef(null);
  const scroll = useRef(null);
  const initialScroll = useRef(true);
  const positionedWindow = useRef(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    let active = true;
    const saved = sessionStorage.getItem(`phone-epg-operation:${targetId}`);
    let pending; try { pending = JSON.parse(sessionStorage.getItem(`phone-epg-request:${targetId}`)); } catch (_) { /* Ignore invalid local state. */ }
    if (pending?.requestId) {
      setRequest(pending);
      getRemoteEpgOperationByRequest(targetId, pending.requestId).then((result) => {
        if (active) { setOperation(result); setRequest(null); sessionStorage.removeItem(`phone-epg-request:${targetId}`); }
      }).catch(() => { if (active) setError('The previous request is still uncertain. Recover its status before another channel change.'); });
    } else if (saved) getRemoteEpgOperation(targetId, saved).then((result) => { if (active) setOperation(result); }).catch(() => {});
    return () => { active = false; };
  }, [targetId]);
  useEffect(() => { if (operation?.id) sessionStorage.setItem(`phone-epg-operation:${targetId}`, String(operation.id)); }, [targetId, operation?.id]);
  const busy = sending || running(operation);
  const timeZone = data.timeZone || 'UTC';
  const today = venueDate(new Date(tick), timeZone);
  const pendingNavigation = navigation && String(navigation.targetId) === String(targetId) ? navigation : null;
  const searchAt = pendingNavigation ? Date.parse(pendingNavigation.result.programme.startsAt) : view.at;
  const day = searchAt !== undefined ? venueDate(new Date(Math.max(searchAt, tick)), timeZone) : view.date > today ? view.date : today;
  const start = Date.parse(sportsWindow(day, 1, timeZone).from);
  const isToday = day === today;
  const nowPosition = useCallback(() => Math.max(0, Math.min(WIDTH, (Date.now() - start) / (24 * HOUR) * WIDTH)), [start]);
  const releaseSearch = () => {
    const button = scroll.current?.querySelector('[aria-current="true"]');
    if (button && document.activeElement === button) scroll.current.focus({ preventScroll: true });
    setSearchTarget(null); setNavigationError('');
    positionedTarget.current = null; focusedTarget.current = null;
  };
  useEffect(() => {
    if (!enabled || !navigation || String(navigation.targetId) !== String(targetId)) return;
    initialScroll.current = false;
    setSearchTarget({ ...navigation.result }); setNavigationError('');
    setView({ at: Date.parse(navigation.result.programme.startsAt) });
    onNavigationAccepted?.(navigation);
  }, [enabled, targetId, navigation, onNavigationAccepted]);
  useEffect(() => {
    if (!enabled) { setLastTuned(null); tuningRoute.current = null; }
  }, [enabled]);
  useEffect(() => {
    if (!operation || running(operation) || !tuningRoute.current) return;
    // Command completion is not channel telemetry; label the route "Last tuned".
    if (enabled && operation.status === 'completed_unverified') setLastTuned(tuningRoute.current);
    tuningRoute.current = null;
  }, [operation, enabled]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { onBusyChange?.(busy); return () => onBusyChange?.(false); }, [busy, onBusyChange]);
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => setTick(Date.now()), 15000);
    const refresh = setInterval(() => setReload((value) => value + 1), 60000);
    return () => { clearInterval(timer); clearInterval(refresh); };
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !targetId) return undefined;
    let active = true; const abort = new AbortController();
    setLoading(true); setError(''); setSelection(null);
    (async () => {
      let channels = []; let result;
      for (let page = 1; page <= 50; page++) {
        result = await getRemoteEpg(targetId, { page, limit: 100,
          startsAt: new Date(start).toISOString(), endsAt: new Date(start + 24 * HOUR).toISOString() }, abort.signal);
        channels.push(...result.channels);
        if (channels.length >= result.total) break;
        if (!result.channels.length || page === 50) throw new Error('The channel list is too large. Reduce the enabled lineup in Setup.');
      }
      if (active) setData({ ...result, channels, windowStart: start });
    })().catch((failure) => { if (active) { setError(failure.message); setData({ channels: [], apps: [], timeZone: 'UTC' }); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; abort.abort(); };
    // Keep the window anchored as live time advances; swiping must not jump back on refresh.
  }, [targetId, enabled, start, reload]);
  useLayoutEffect(() => {
    if (!enabled || !scroll.current) return;
    if (initialScroll.current || (positionedWindow.current !== start && isToday && !searchTarget)) {
      scroll.current.scrollLeft = nowPosition();
      initialScroll.current = false;
    }
    positionedWindow.current = start;
  }, [enabled, start, view, isToday, searchTarget, nowPosition]);
  useLayoutEffect(() => {
    const element = scroll.current;
    if (!enabled || !element) return undefined;
    const measure = () => {
      if (element.clientHeight) setViewport({ top: element.scrollTop, height: element.clientHeight });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [enabled]);
  useEffect(() => {
    if (!running(operation)) return undefined;
    let active = true; let timer;
    const poll = async () => {
      try {
        const result = await getRemoteEpgOperation(targetId, operation.id);
        if (active) { setOperation(result); if (running(result)) timer = setTimeout(poll, 1000); }
      } catch { if (active) setError('Channel change status is unknown. Check the TV, then check status.'); }
    };
    timer = setTimeout(poll, 500);
    return () => { active = false; clearTimeout(timer); };
  }, [targetId, operation?.id, operation?.status, statusReload]);
  const play = async (channel, retry = false) => {
    if (inFlight.current || blocked || sending || running(operation) || !retry && request) return;
    let body = request;
    if (!retry) {
      const confirmShared = channel.shared && window.confirm('This player feeds multiple screens. Change the channel on all affected screens?');
      if (channel.shared && !confirmShared) return;
      setLastTuned(null);
      body = { appKey: channel.appKey, channelId: channel.channelId, profileRevision: channel.profileRevision,
        channelRevision: channel.channelRevision, confirmShared: Boolean(confirmShared),
        requestId: window.crypto?.randomUUID?.() || `remote-${Date.now()}-${Math.random().toString(36).slice(2)}` };
      sessionStorage.setItem(`phone-epg-request:${targetId}`, JSON.stringify(body));
      setRequest(body);
    }
    tuningRoute.current = channel?.id || data.channels.find((entry) => entry.appKey === body?.appKey && entry.channelId === body?.channelId)?.id || null;
    inFlight.current = true; setSending(true); setError(''); setOperation(null);
    try {
      const result = retry ? await getRemoteEpgOperationByRequest(targetId, body.requestId) : await watchRemoteEpg(targetId, body);
      sessionStorage.setItem(`phone-epg-operation:${targetId}`, String(result.id));
      sessionStorage.removeItem(`phone-epg-request:${targetId}`);
      if (alive.current) { setOperation(result); setRequest(null); }
    }
    catch (failure) {
      if (alive.current) {
        const rejected = !retry && [400, 401, 403, 404, 409].includes(failure.status);
        if (rejected) { setRequest(null); sessionStorage.removeItem(`phone-epg-request:${targetId}`); }
        setError(`${failureText[failure.message] || 'Channel request could not be confirmed.'} Check the TV. ${rejected ? 'The request was rejected before a new operation was admitted.' : 'Recover this request to check its result without sending a new selection.'}`);
      }
    }
    finally { inFlight.current = false; if (alive.current) setSending(false); }
  };
  const goNow = () => {
    releaseSearch(); setSelection(null); setSearchReset((value) => value + 1);
    initialScroll.current = true;
    setView({}); setTick(Date.now());
  };
  const moveDay = (direction) => {
    const next = epgShiftDay(day, direction);
    if (next <= today) { goNow(); return; }
    if (next > epgShiftDay(today, 14)) return;
    releaseSearch(); setSelection(null); setSearchReset((value) => value + 1); initialScroll.current = false;
    setView({ date: next });
    if (scroll.current) scroll.current.scrollLeft = 0;
  };
  const canPlay = !blocked && !busy && !request && !loading;
  const playAd = (rotation) => {
    if (!canPlay || !adDevice || adStatus !== 'ready') return;
    setLastTuned(null); tuningRoute.current = null; setOperation(null); setError('');
    onPlayAd(adDevice, rotation);
  };
  const bars = rotations.filter((entry) => entry.showInEpg);
  const allRows = [...data.channels.map((channel) => ({ channel, ad: false })), ...bars.map((channel) => ({ channel, ad: true }))];
  const firstRow = Math.max(0, Math.min(allRows.length, Math.floor(Math.max(0, viewport.top - 28) / ROW_HEIGHT) - 4));
  const lastRow = Math.min(allRows.length, firstRow + Math.ceil(viewport.height / ROW_HEIGHT) + 9);
  useLayoutEffect(() => {
    if (!enabled || loading || !searchTarget || data.windowStart !== start || !scroll.current) return;
    const index = data.channels.findIndex((channel) => channel.id === searchTarget.channel.id);
    const programme = data.channels[index]?.programmes?.find((entry) => entry.id === searchTarget.programme.id);
    if (!programme || !epgProgrammeStyle(programme, start)) {
      setNavigationError('This listing has changed. Search again for its latest schedule.');
      return;
    }
    const element = scroll.current;
    if (positionedTarget.current?.target !== searchTarget || positionedTarget.current?.start !== start) {
      positionedTarget.current = { target: searchTarget, start };
      element.scrollTop = index * ROW_HEIGHT;
      element.scrollLeft = Math.max(isToday ? nowPosition() : 0, (Math.max(start, Date.parse(programme.startsAt)) - start) / (24 * HOUR) * WIDTH - 20);
      setViewport((current) => ({ top: element.scrollTop, height: element.clientHeight || current.height }));
      setSelection(`${searchTarget.channel.id}`);
    }
    // The target row may only be mounted after updating the virtual viewport.
    const button = element.querySelector('[aria-current="true"]');
    if (button && (focusedTarget.current?.target !== searchTarget || focusedTarget.current?.start !== start)) {
      focusedTarget.current = { target: searchTarget, start };
      button.focus({ preventScroll: true });
    }
  }, [enabled, loading, searchTarget, data, start, firstRow, lastRow, isToday, nowPosition]);
  const nowLeft = (tick - start) / (24 * HOUR) * 100;
  const row = (channel, ad = false) => <div className="remote-epg__row" key={ad ? `ad:${channel.id}` : channel.id}
    data-last-tuned={!ad && lastTuned === channel.id ? 'true' : undefined}>
    <div className="remote-epg__channel">
      <div className="remote-epg__channel-heading"><button type="button" className="remote-epg__name" aria-expanded={selection === `${ad ? 'ad:' : ''}${channel.id}`}
        onClick={() => setSelection((value) => value === `${ad ? 'ad:' : ''}${channel.id}` ? null : `${ad ? 'ad:' : ''}${channel.id}`)}>{channel.name}</button>
        {!ad && lastTuned === channel.id && <span className="remote-epg__tuned-badge" title="Last tuning command completed through this guide; playback is unverified.">Last tuned</span>}</div>
      <small>{ad ? `Advertising · ${adDevice?.target?.name || 'No player'}` : `${channel.appName} · ${channel.deviceName}`}</small>
      {selection === `${ad ? 'ad:' : ''}${channel.id}` && <button type="button" className="remote-epg__play"
        disabled={!canPlay || (ad ? !adDevice || !channel.itemCount || adStatus !== 'ready' : !channel.canWatch)}
        onClick={() => ad ? playAd(channel) : play(channel)}>Play Now</button>}
      {!ad && !channel.canWatch && <><small>{watchReadinessText(channel)}</small>
        <a href={watchSetupHref(channel)}>Set up Watch Now</a></>}
      {ad && !channel.itemCount && <small>Empty rotation</small>}
    </div>
    <div className="remote-epg__track">
      {nowLeft >= 0 && nowLeft <= 100 && <span className="remote-epg__now" style={{ left: `${nowLeft}%` }} aria-label="Current time" />}
      {ad ? <button type="button" className="remote-epg__programme remote-epg__programme--ad" disabled={!canPlay || !adDevice || !channel.itemCount || adStatus !== 'ready'}
        onClick={() => playAd(channel)}><strong>{channel.name}</strong><small>All day · Play rotation</small></button>
        : !(channel.programmes || []).length ? <span className="remote-epg__gap">No listings in this window</span>
          : channel.programmes.map((programme) => {
            if (isToday && Date.parse(programme.endsAt) <= tick) return null;
            const style = epgProgrammeStyle(programme, start);
            return style && <button type="button" className="remote-epg__programme" key={programme.id} style={style}
              data-tone={epgProgrammeTone(programme.title)}
              data-on-now={Date.parse(programme.startsAt) <= tick && Date.parse(programme.endsAt) > tick ? 'true' : undefined}
              aria-current={searchTarget?.channel.id === channel.id && searchTarget?.programme.id === programme.id ? 'true' : undefined}
              onClick={() => setSelection(`${channel.id}`)} title={`${programme.title} · ${clock(Date.parse(programme.startsAt), timeZone)}–${clock(Date.parse(programme.endsAt), timeZone)}`}>
              <small>{clock(Date.parse(programme.startsAt), timeZone)}–{clock(Date.parse(programme.endsAt), timeZone)}</small><strong>{programme.title}</strong></button>;
          })}
    </div>
  </div>;
  return <section className="remote-epg" aria-label="TV programme guide">
    <div className="remote-epg__header"><RemoteEpgSearch key={targetId} targetId={targetId} enabled={enabled} timeZone={timeZone} resetKey={searchReset} onClear={releaseSearch} onNavigate={(result) => {
      initialScroll.current = false; setSearchTarget(result); setNavigationError('');
      setView({ at: Date.parse(result.programme.startsAt) });
    }} />
      <nav className="remote-epg__days" aria-label="Guide day navigation">
        {!isToday && <button type="button" aria-label="Previous day" onClick={() => moveDay(-1)}>«</button>}
        <button type="button" className="remote-epg__day-label" aria-label="Go to Now" title="Return to current time" onClick={goNow}>{isToday ? 'Now' : epgDayLabel(day)}</button>
        <button type="button" aria-label="Next day" disabled={day >= epgShiftDay(today, 14)} onClick={() => moveDay(1)}>»</button>
      </nav>
    </div>
    <div className="remote-epg__toolbar">
      <span>{new Date(start).toLocaleDateString('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short' })} · {timeZone}</span>
      <button type="button" disabled={loading} onClick={() => { setReload((value) => value + 1); onRefresh?.(); }}>Refresh</button>
    </div>
    <NativeOperationControls key={`${targetId}:${operation?.id}`} operation={operation}
      onContinue={() => continueRemoteEpgOperation(targetId, operation.id)} onCancel={() => cancelRemoteEpgOperation(targetId, operation.id)}
      onResult={setOperation} onError={(failure) => setError(failure.message)}
      onExport={async () => downloadNativeOperationReport(await exportRemoteEpgOperation(targetId, operation.id), operation.id)} />
    <div className="remote-epg__status" aria-live="polite" data-idle={!loading && !error && !navigationError && !operation && !request && !adMessage}>
      {loading ? 'Loading channels…' : error || navigationError || (running(operation) ? `Changing channel · ${(operation.stage || 'queued').replaceAll('_', ' ')}…`
        : operation ? failureText[operation.message] || operation.result?.message || `Channel change: ${operation.status}. Check the TV picture.`
          : adMessage || `${data.channels.length} channels · Tap a channel for Play Now`)}
      {request && !busy && <button type="button" disabled={blocked} onClick={() => play(null, true)}>Recover request</button>}
      {running(operation) && <button type="button" onClick={() => { setError(''); setStatusReload((value) => value + 1); }}>Check status</button>}
    </div>
    {!loading && !error && !data.channels.length && !bars.length && <p>Select channels and connect app guides in site Setup to populate this TV’s EPG.</p>}
    {adStatus === 'error' && <p role="alert">Ad rotations could not be loaded. Refresh the Launcher.</p>}
    <div className="remote-epg__scroll" ref={scroll} role="region" aria-label="Channel timeline" tabIndex={0}
      onPointerDown={releaseSearch} onWheel={releaseSearch}
      onKeyDown={(event) => { if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) releaseSearch(); }}
      onScroll={(event) => {
        const element = event.currentTarget;
        if (isToday && element.scrollLeft < nowPosition() - 1) element.scrollLeft = nowPosition();
        setViewport((current) => current.top === element.scrollTop && current.height === element.clientHeight ? current
          : { top: element.scrollTop, height: element.clientHeight || current.height });
      }}>
      <div className="remote-epg__grid" style={{ '--remote-epg-timeline-width': `${WIDTH}px` }}>
        <div className="remote-epg__row remote-epg__axis"><div className="remote-epg__channel">Channel</div><div className="remote-epg__track">
          {Array.from({ length: 48 }, (_, index) => <time key={index} style={{ left: `${100 * index / 48}%` }}>{clock(start + index * HOUR / 2, timeZone)}</time>)}
        </div></div>
        <div style={{ height: firstRow * ROW_HEIGHT }} aria-hidden="true" />
        {allRows.slice(firstRow, lastRow).map((entry) => row(entry.channel, entry.ad))}
        <div style={{ height: (allRows.length - lastRow) * ROW_HEIGHT }} aria-hidden="true" />
      </div>
    </div>
  </section>;
}

export default function RemoteEpg(props) {
  return <RemoteEpgView key={props.targetId} {...props} />;
}
